/**
 * Gestion des utilisateurs — ADMIN uniquement
 * Liste + modification profil + toggle actif
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/shared/services/apiClient';
import { API_ENDPOINTS } from '@/config/api';
import type { User } from '@/shared/types/user';
import { PROFIL_LABELS, type ProfilEnum } from '@/shared/types/user';

const PROFILS = ['IDE', 'médecin', 'AS', 'secrétaire', 'cadre', 'admin'] as const;

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null); // userId en cours de sauvegarde

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.get<User[]>(API_ENDPOINTS.adminUsers + '?actif_only=false');
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const updateUser = async (userId: string, patch: { profil?: string; actif?: boolean }) => {
    setSaving(userId);
    try {
      const updated = await apiClient.patch<User>(API_ENDPOINTS.adminUser(userId), patch);
      setUsers((prev) => prev.map((u) => (u.id === userId ? updated : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la mise à jour');
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h1 className="h4 mb-0">
            <i className="bi bi-people-fill me-2 text-primary"></i>Gestion des utilisateurs
          </h1>
          <small className="text-muted">{users.length} utilisateur(s) — modification profil et statut</small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={fetchUsers}>
          <i className="bi bi-arrow-clockwise me-1"></i>Actualiser
        </button>
      </div>

      {error && (
        <div className="alert alert-danger d-flex align-items-center mb-4">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error}
          <button className="btn-close ms-auto" onClick={() => setError(null)} />
        </div>
      )}

      <div className="card card-premium">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="table-light">
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Service</th>
                <th>Profil</th>
                <th>Statut</th>
                <th>Créé le</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td className="fw-semibold">
                    {user.prenom} {user.nom}
                  </td>
                  <td className="text-muted" style={{ fontSize: '0.875rem' }}>
                    {user.email}
                  </td>
                  <td>
                    <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                      {user.service || <em className="text-muted">—</em>}
                    </span>
                  </td>
                  <td>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: 'auto', minWidth: '140px' }}
                      value={user.profil}
                      disabled={saving === user.id}
                      onChange={(e) => updateUser(user.id, { profil: e.target.value })}
                    >
                      {PROFILS.map((p) => (
                        <option key={p} value={p}>{PROFIL_LABELS[p as ProfilEnum]}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <div className="form-check form-switch mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        id={`actif-${user.id}`}
                        checked={user.actif}
                        disabled={saving === user.id}
                        onChange={(e) => updateUser(user.id, { actif: e.target.checked })}
                      />
                      <label className="form-check-label" htmlFor={`actif-${user.id}`}>
                        {saving === user.id ? (
                          <span className="spinner-border spinner-border-sm text-primary" />
                        ) : (
                          <span className={`badge ${user.actif ? 'bg-success' : 'bg-secondary'}`}>
                            {user.actif ? 'Actif' : 'Inactif'}
                          </span>
                        )}
                      </label>
                    </div>
                  </td>
                  <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
